// src/components/form/CustomFormEditor.jsx
import * as React from 'react';
import { useEffect, useState } from 'react';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { DateTimePicker } from '@progress/kendo-react-dateinputs';
import { Checkbox } from '@progress/kendo-react-inputs';
import config from './../config/config';

export const CustomFormEditor = (props) => {
  const { dataItem, resources, fields, onSubmit, onCancel } = props;
  const [jobData, setJobData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchJobData = async () => {
      if (!dataItem || !dataItem.originalJobID) {
        setJobData({
          start: dataItem?.start || new Date(),
          end: dataItem?.end || new Date(),
          title: dataItem?.[fields.title] || '',
          description: dataItem?.[fields.description] || '',
          employeeID: dataItem?.employeeID || null,
          id: dataItem?.id || null,
          originalJobID: dataItem?.originalJobID || null,
          isAllDay: dataItem?.isAllDay || false,
        });
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${config.host}/jobs/${dataItem.originalJobID}`);
        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
        const job = await response.json();

        setJobData({
          start: new Date(job.AusfuehrungszeitStart),
          end: new Date(job.AusfuehrungszeitEnde),
          title: dataItem[fields.title] || '',
          description: dataItem[fields.description] || '',
          employeeID: dataItem.employeeID || null,
          id: dataItem.id,
          originalJobID: dataItem.originalJobID,
          isAllDay: job.isAllDay || false, // Lese isAllDay aus der API, falls vorhanden
        });
        setLoading(false);
      } catch (error) {
        console.error('Error fetching job data:', error);
        setLoading(false);
      }
    };
    fetchJobData();
  }, [dataItem, fields]);

  useEffect(() => {
    const actionButtons = document.querySelector('.k-dialog .k-actions.k-window-actions.k-dialog-actions');
    if (actionButtons) actionButtons.style.display = 'none';
  }, []);

  if (loading || !resources) return <div>Loading...</div>;
  if (!jobData) return <div>Error loading job data: Keine gültigen Daten verfügbar</div>;

  const employees = resources[0]?.data || [];

  const handleStartChange = (e) => {
    setJobData((prev) => ({ ...prev, start: e.value }));
  };

  const handleEndChange = (e) => {
    setJobData((prev) => ({ ...prev, end: e.value }));
  };

  const handleAllDayChange = (e) => {
    const isAllDay = e.value;
    setJobData((prev) => ({
      ...prev,
      isAllDay,
      start: isAllDay ? new Date(prev.start.setHours(0, 0, 0, 0)) : prev.start,
      end: isAllDay ? new Date(prev.end.setHours(23, 59, 59, 999)) : prev.end,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
  
    const correctedStart = new Date(jobData.start);
    const correctedEnd = new Date(jobData.end);
  
    const updatedDataFull = {
      ...dataItem,
      id: jobData.id, // Sicherstellen, dass die ID erhalten bleibt
      originalJobID: jobData.originalJobID,
      start: correctedStart,
      end: correctedEnd,
      title: jobData.title,
      description: jobData.description,
      employeeID: jobData.employeeID,
      employeeColor: jobData.employeeColor,
      isAllDay: jobData.isAllDay,
    };
  
    const { employeeColor, ...updatedData } = updatedDataFull;
  
    const payload = {
      AusfuehrungszeitStart: correctedStart.toISOString(),
      AusfuehrungszeitEnde: correctedEnd.toISOString(),
      isAllDay: jobData.isAllDay,
    };
  
    if (updatedData.originalJobID) {
      try {
        const response = await fetch(`${config.host}/jobs/${updatedData.originalJobID}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
        await response.json();
        console.log(`Job ${updatedData.originalJobID} updated successfully`);
      } catch (error) {
        console.error(`Error updating job ${updatedData.originalJobID}:`, error);
      }
    }
  
    // Übergib die vollständigen Daten an onSubmit
    onSubmit({ updated: [updatedDataFull] });
  };

  const handleCancel = (e) => {
    e.preventDefault();
    onCancel();
  };

  return (
    <div className="custom-scheduler-form-wrapper">
      <div className="custom-scheduler-form-content">
        <div className="form-field">
          <label>Title</label>
          <input type="text" value={jobData.title || ''} readOnly />
        </div>

        <div className="form-field form-field-description">
          <label>Description</label>
          <div className="description-display">{jobData.description || ''}</div>
        </div>

        <div className="form-field">
          <label>Start</label>
          <DateTimePicker
            value={jobData.start}
            onChange={handleStartChange}
            format="dd.MM.yyyy HH:mm"
            width={200}
          />
        </div>

        <div className="form-field">
          <label>End</label>
          <DateTimePicker
            value={jobData.end}
            onChange={handleEndChange}
            format="dd.MM.yyyy HH:mm"
            width={200}
          />
        </div>

        <div className="form-field form-field-all-day">
          <label>All Day Event</label>
          <Checkbox
            checked={jobData.isAllDay || false}
            onChange={handleAllDayChange}
          />
        </div>

        <div className="form-field">
          <label>Employee</label>
          <DropDownList
            data={employees}
            textField="employeeName"
            dataItemKey="employeeID"
            value={employees.find((emp) => emp.employeeID === jobData.employeeID) || null}
            onChange={(e) =>
              setJobData((prev) => ({
                ...prev,
                employeeID: e.value ? e.value.employeeID : null,
              }))
            }
            style={{ width: '100%' }}
          />
        </div>
      </div>

      <div className="form-buttons form-buttons-spaced">
        <button className="k-button k-button-md k-button-solid k-button-solid-primary" onClick={handleSubmit}>
          Save
        </button>
        <button className="k-button k-button-md k-button-solid k-button-solid-base" onClick={handleCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
};

export default CustomFormEditor;